(() => {
  const rootPath = window.Shopify?.routes?.root || '/';
  const currency = window.Shopify?.currency?.active || 'USD';
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format((cents || 0) / 100);
  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));
  let translations = {};
  try { translations = JSON.parse(document.querySelector('[data-auzy-translations]')?.textContent || '{}'); } catch (error) { translations = {}; }
  const translate = (key, replacements = {}) => {
    let value = translations[key] || '';
    Object.entries(replacements).forEach(([name, replacement]) => { value = value.replaceAll(`__${name}__`, String(replacement)); });
    return value;
  };
  const selectors = {
    drawer: '[data-auzy-cart-drawer]',
    backdrop: '[data-auzy-cart-backdrop]',
    items: '[data-auzy-cart-items]',
    footer: '[data-auzy-cart-footer]',
    status: '[data-auzy-cart-status]'
  };

  const drawer = () => document.querySelector(selectors.drawer);
  const setDrawer = (open) => {
    const panel = drawer();
    const backdrop = document.querySelector(selectors.backdrop);
    if (!panel || !backdrop) return;
    panel.classList.toggle('is-open', open);
    panel.setAttribute('aria-hidden', String(!open));
    backdrop.hidden = !open;
    document.body.classList.toggle('drawer-open', open);
    if (open) panel.querySelector('[data-auzy-cart-close]')?.focus();
  };

  const renderCart = (cart) => {
    document.querySelectorAll('[data-auzy-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
      const link = count.closest('[data-auzy-cart-open]');
      if (link) link.setAttribute('aria-label', translate(cart.item_count === 1 ? 'cartItem' : 'cartItems', { count: cart.item_count }));
    });
    document.querySelectorAll('[data-auzy-drawer-count]').forEach((count) => { count.textContent = translate(cart.item_count === 1 ? 'item' : 'items', { count: cart.item_count }); });
    document.querySelectorAll('[data-auzy-cart-total]').forEach((total) => { total.textContent = money(cart.total_price); });
    const container = document.querySelector(selectors.items);
    const footer = document.querySelector(selectors.footer);
    if (!container || !footer) return;
    footer.hidden = cart.item_count === 0;
    if (!cart.items.length) {
      container.innerHTML = `<div class="auzy-cart-empty"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 4h2l2.1 11h10.8l2-8H6M9 20h.01M17 20h.01"/></svg><strong>${escapeHtml(translate('empty'))}</strong><span>${escapeHtml(translate('emptyCopy'))}</span></div>`;
      return;
    }
    container.innerHTML = cart.items.map((item, index) => {
      const image = item.image ? `<img src="${escapeHtml(item.image)}&width=180" alt="${escapeHtml(item.product_title)}" width="90" height="90" loading="lazy">` : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<small>${escapeHtml(item.variant_title)}</small>` : '';
      const decrease = escapeHtml(translate('decrease', { title: item.product_title }));
      const increase = escapeHtml(translate('increase', { title: item.product_title }));
      return `<article class="auzy-mini-item" data-line="${index + 1}"><a href="${escapeHtml(item.url)}">${image}</a><div><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>${variant}<strong>${money(item.final_line_price)}</strong><div class="auzy-mini-item__quantity"><button type="button" data-auzy-cart-change="-1" aria-label="${decrease}">−</button><span>${item.quantity}</span><button type="button" data-auzy-cart-change="1" aria-label="${increase}">+</button><button class="auzy-mini-item__remove" type="button" data-auzy-cart-remove>${escapeHtml(translate('remove'))}</button></div></div></article>`;
    }).join('');
  };

  const fetchCart = async () => {
    const response = await fetch(`${rootPath}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(translate('loadError'));
    return response.json();
  };

  const changeCartLine = async (line, quantity) => {
    const response = await fetch(`${rootPath}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    if (!response.ok) throw new Error(translate('updateError'));
    return response.json();
  };

  const initializeHeader = () => {
    const header = document.querySelector('[data-auzy-header]');
    if (!header || header.dataset.ready === 'true') return;
    header.dataset.ready = 'true';
    const menuButton = header.querySelector('[data-auzy-menu-toggle]');
    const navigation = header.querySelector('[data-auzy-nav]');
    const searchButton = header.querySelector('[data-auzy-search-toggle]');
    const searchPanel = header.querySelector('[data-auzy-search-panel]');
    menuButton?.addEventListener('click', () => {
      const open = !navigation.classList.contains('is-open');
      navigation.classList.toggle('is-open', open);
      menuButton.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('menu-open', open);
    });
    searchButton?.addEventListener('click', () => {
      const open = searchPanel.hidden;
      searchPanel.hidden = !open;
      searchButton.setAttribute('aria-expanded', String(open));
      if (open) searchPanel.querySelector('input[type="search"]')?.focus();
    });
    document.addEventListener('click', (event) => {
      if (!searchPanel || searchPanel.hidden || header.contains(event.target)) return;
      searchPanel.hidden = true;
      searchButton?.setAttribute('aria-expanded', 'false');
    });
    const scrollContainer = document.querySelector('.page-wrapper');
    const getScrollOffset = () => scrollContainer?.scrollTop || window.scrollY || 0;
    let scrollFrame = 0;
    const updateHeader = () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(() => {
        header.classList.toggle('is-scrolled', getScrollOffset() > 24);
        scrollFrame = 0;
      });
    };
    updateHeader();
    (scrollContainer || window).addEventListener('scroll', updateHeader, { passive: true });
  };

  const initializeSlider = (store) => {
    if (store.dataset.sliderReady === 'true') return;
    store.dataset.sliderReady = 'true';
    const slides = [...store.querySelectorAll('[data-auzy-slide]')];
    const dots = [...store.querySelectorAll('[data-auzy-dot]')];
    if (slides.length < 2) return;
    let index = 0;
    let timer;
    const show = (target) => {
      index = (target + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => { const active = slideIndex === index; slide.hidden = !active; slide.classList.toggle('is-active', active); });
      dots.forEach((dot, dotIndex) => { const active = dotIndex === index; dot.classList.toggle('is-active', active); dot.setAttribute('aria-pressed', String(active)); });
    };
    const start = () => {
      window.clearInterval(timer);
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) timer = window.setInterval(() => show(index + 1), 7000);
    };
    store.querySelector('[data-auzy-slide-prev]')?.addEventListener('click', () => { show(index - 1); start(); });
    store.querySelector('[data-auzy-slide-next]')?.addEventListener('click', () => { show(index + 1); start(); });
    dots.forEach((dot) => dot.addEventListener('click', () => { show(Number(dot.dataset.auzyDot)); start(); }));
    store.addEventListener('mouseenter', () => window.clearInterval(timer));
    store.addEventListener('mouseleave', start);
    store.addEventListener('focusin', () => window.clearInterval(timer));
    store.addEventListener('focusout', start);
    start();
  };

  const initializeCatalog = (catalog) => {
    if (catalog.dataset.ready === 'true') return;
    catalog.dataset.ready = 'true';
    const buttons = [...catalog.querySelectorAll('[data-auzy-filter]')];
    const items = [...catalog.querySelectorAll('[data-auzy-catalog-item]')];
    const count = catalog.querySelector('[data-auzy-result-count]');
    buttons.forEach((button) => button.addEventListener('click', () => {
      const filter = button.dataset.auzyFilter;
      buttons.forEach((item) => { const active = item === button; item.classList.toggle('is-active', active); item.setAttribute('aria-pressed', String(active)); });
      let visible = 0;
      items.forEach((item) => { const show = filter === 'all' || item.dataset.category === filter; item.hidden = !show; if (show) visible += 1; });
      if (count) count.textContent = (visible === 1 ? catalog.dataset.countOne : catalog.dataset.countMany).replaceAll('__count__', String(visible));
    }));
    const sort = catalog.querySelector('[data-auzy-sort]');
    sort?.addEventListener('change', () => sort.form.submit());
  };

  const initializeGallery = (gallery) => {
    if (gallery.dataset.ready === 'true') return;
    gallery.dataset.ready = 'true';
    const main = gallery.querySelector('.auzy-product-gallery__main img');
    const thumbs = [...gallery.querySelectorAll('[data-auzy-gallery-thumb]')];
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => {
      if (!main) return;
      main.src = thumb.dataset.src;
      main.srcset = '';
      main.alt = thumb.dataset.alt || '';
      thumbs.forEach((item) => { const active = item === thumb; item.classList.toggle('is-active', active); item.setAttribute('aria-pressed', String(active)); });
    }));
  };

  const initialize = (scope = document) => {
    initializeHeader();
    scope.querySelectorAll('[data-auzy-home]').forEach(initializeSlider);
    scope.querySelectorAll('[data-auzy-catalog]').forEach(initializeCatalog);
    scope.querySelectorAll('[data-auzy-gallery]').forEach(initializeGallery);
  };

  document.addEventListener('click', async (event) => {
    const newsletterClose = event.target.closest('[data-auzy-newsletter-close]');
    if (newsletterClose) {
      const newsletter = newsletterClose.closest('[data-auzy-newsletter]');
      if (newsletter) newsletter.open = false;
      newsletter?.querySelector('summary')?.focus();
      return;
    }
    document.querySelectorAll('[data-auzy-newsletter][open]').forEach((newsletter) => {
      if (!newsletter.contains(event.target)) newsletter.open = false;
    });
    const open = event.target.closest('[data-auzy-cart-open]');
    if (open) { event.preventDefault(); setDrawer(true); return; }
    if (event.target.closest('[data-auzy-cart-close], [data-auzy-cart-backdrop]')) { setDrawer(false); return; }
    const quantityButton = event.target.closest('[data-auzy-quantity]');
    if (quantityButton) {
      const control = quantityButton.closest('.auzy-quantity');
      const input = control?.querySelector('input');
      if (input) input.value = Math.max(Number(input.min || 0), Number(input.value || 0) + Number(quantityButton.dataset.auzyQuantity));
      return;
    }
    const cartButton = event.target.closest('[data-auzy-cart-change], [data-auzy-cart-remove]');
    if (cartButton) {
      const item = cartButton.closest('[data-line]');
      const line = Number(item?.dataset.line);
      const current = Number(item?.querySelector('.auzy-mini-item__quantity span')?.textContent || 0);
      const quantity = cartButton.hasAttribute('data-auzy-cart-remove') ? 0 : Math.max(0, current + Number(cartButton.dataset.auzyCartChange));
      cartButton.disabled = true;
      try { renderCart(await changeCartLine(line, quantity)); } catch (error) { const status = document.querySelector(selectors.status); if (status) status.textContent = error.message; } finally { cartButton.disabled = false; }
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-auzy-add-form]');
    const button = event.submitter;
    if (!form || !button?.matches('.auzy-card-button, .auzy-product-form__add')) return;
    event.preventDefault();
    button.disabled = true;
    button.classList.add('is-loading');
    try {
      const response = await fetch(`${rootPath}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (!response.ok) { const data = await response.json(); throw new Error(data.description || translate('addError')); }
      const cart = await fetchCart();
      renderCart(cart);
      const status = document.querySelector(selectors.status);
      if (status) status.textContent = translate('added');
      setDrawer(true);
    } catch (error) {
      const status = document.querySelector(selectors.status);
      if (status) status.textContent = error.message;
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    setDrawer(false);
    const navigation = document.querySelector('[data-auzy-nav]');
    navigation?.classList.remove('is-open');
    document.body.classList.remove('menu-open');
    const searchPanel = document.querySelector('[data-auzy-search-panel]');
    const searchButton = document.querySelector('[data-auzy-search-toggle]');
    if (searchPanel && !searchPanel.hidden) {
      searchPanel.hidden = true;
      searchButton?.setAttribute('aria-expanded', 'false');
    }
    document.querySelectorAll('[data-auzy-newsletter][open]').forEach((newsletter) => { newsletter.open = false; });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize()); else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
